import { useEffect, useMemo, useState } from 'react';

import MaterialTable from 'material-table';

import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
    odooCatalog,
    odooOverview,
    updateOdooVariantSettings
} from '../../../../actions/admin/odoo_product';
import { exportCsv } from '../../../../helpers/csv/csvHelper';
import {
    getOdooLinkRowStyle,
    getOdooStockSyncState,
    isOdooStockSyncEnabled
} from '../../../../helpers/admin/odooStockSyncHelper';
import OdooStockSyncSwitch from './OdooStockSyncSwitch';

const tableLocalization = {
    body: {
        emptyDataSourceMessage: 'No hay datos por mostrar'
    },
    pagination: {
        labelDisplayedRows: '{from}-{to} de {count}',
        labelRowsSelect: 'filas',
        labelRowsPerPage: 'Filas por página:',
        firstTooltip: 'Primera página',
        previousTooltip: 'Página anterior',
        nextTooltip: 'Página siguiente',
        lastTooltip: 'Última página'
    },
    toolbar: {
        exportTitle: 'Exportar',
        exportName: 'Exportar como CSV',
        searchTooltip: 'Buscar',
        searchPlaceholder: 'Buscar'
    }
};

const tableOptions = fileName => ({
    pageSize: 10,
    pageSizeOptions: [10, 20, 50],
    search: true,
    exportButton: true,
    exportCsv: exportCsv(fileName),
    actionsColumnIndex: -1
});

const formatDate = value => {
    if (!value)
        return 'Aún no disponible';

    return new Intl.DateTimeFormat('es-MX', {
        dateStyle: 'medium',
        timeStyle: 'medium',
        timeZone: 'America/Mexico_City'
    }).format(new Date(value));
};

const money = value => new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
}).format(Number(value) || 0);

const OdooProductList = () => {
    const [variants, setVariants] = useState([]);
    const [syncStatus, setSyncStatus] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [catalogDate, setCatalogDate] = useState(null);
    const [catalogError, setCatalogError] = useState(false);
    const [localLoading, setLocalLoading] = useState(true);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [savingIds, setSavingIds] = useState([]);
    const [stockFilter, setStockFilter] = useState('all');
    const [dialogVariant, setDialogVariant] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [manualId, setManualId] = useState('');
    const [dialogSync, setDialogSync] = useState(true);
    const [dialogSaving, setDialogSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const loadOverview = async () => {
        setLocalLoading(true);
        const result = await odooOverview();

        if (result.status) {
            setVariants(result.resp.variants || []);
            setSyncStatus(result.resp.sync_status || null);
        } else {
            setMessage({ severity: 'error', text: 'No se pudieron cargar las variantes locales.' });
        }

        setLocalLoading(false);
    };

    const loadCatalog = async () => {
        setCatalogLoading(true);
        setCatalogError(false);
        const result = await odooCatalog();
        if (result.status) {
            setCatalog(result.resp.products || []);
            setCatalogDate(result.resp.consulted_at);
        } else {
            setCatalogError(true);
        }

        setCatalogLoading(false);
    };

    useEffect(() => {
        loadOverview();
        loadCatalog();
    }, []);

    const catalogIds = useMemo(
        () => new Set(catalog.map(product => String(product.id))),
        [catalog]
    );
    const linkedVariants = variants.filter(variant => variant.odoo_id);
    const unlinkedVariants = variants.filter(variant => !variant.odoo_id);
    const visibleCatalog = catalog.filter(product => {
        const stock = Number(product.stock) || 0;

        if (stockFilter === 'available')
            return stock > 0;

        if (stockFilter === 'empty')
            return stock <= 0;

        return true;
    });

    const stockSyncStatus = variant => {
        const state = getOdooStockSyncState(variant);

        return (
            <Tooltip title={state.description} arrow>
                <Chip color={state.color} size="small" label={state.label} />
            </Tooltip>
        );
    };

    const toggleSync = async variant => {
        const previousValue = isOdooStockSyncEnabled(variant);
        const nextValue = !previousValue;

        setSavingIds(ids => [...ids, variant._id]);
        setVariants(current => current.map(item =>
            item._id === variant._id ?
                { ...item, sync_odoo_stock: nextValue }
            :
                item
        ));

        const result = await updateOdooVariantSettings(variant._id, {
            odoo_id: variant.odoo_id,
            sync_odoo_stock: nextValue
        });

        if (!result.status) {
            setVariants(current => current.map(item =>
                item._id === variant._id ?
                    { ...item, sync_odoo_stock: previousValue }
                :
                    item
            ));
            setMessage({
                severity: 'error',
                text: result.resp?.message || 'No se pudo cambiar la sincronización.'
            });
        } else {
            await loadOverview();
        }

        setSavingIds(ids => ids.filter(id => id !== variant._id));
    };

    const openLinkDialog = variant => {
        const currentProduct = catalog.find(product =>
            String(product.id) === String(variant.odoo_id)
        ) || null;

        setDialogVariant(variant);
        setSelectedProduct(currentProduct);
        setManualId(variant.odoo_id || '');
        setDialogSync(variant.sync_odoo_stock ?? true);
    };

    const closeDialog = () => {
        if (!dialogSaving)
            setDialogVariant(null);
    };

    const saveLink = async () => {
        const odooId = selectedProduct ?
            String(selectedProduct.id)
        :
            manualId.trim();

        if (dialogSync && !odooId) {
            setMessage({
                severity: 'warning',
                text: 'Indica un ID de Odoo o desactiva la sincronización.'
            });
            return;
        }

        setDialogSaving(true);
        const result = await updateOdooVariantSettings(dialogVariant._id, {
            odoo_id: odooId,
            sync_odoo_stock: dialogSync
        });

        if (result.status) {
            setDialogVariant(null);
            setMessage({ severity: 'success', text: 'Vinculación actualizada.' });
            await loadOverview();
        } else {
            setMessage({
                severity: 'error',
                text: result.resp?.message || 'No se pudo actualizar la vinculación.'
            });
        }

        setDialogSaving(false);
    };

    const linkedColumns = [
        { title: 'Producto + variante', field: 'full_name' },
        { title: 'ID Odoo', field: 'odoo_id' },
        {
            title: 'Nombre en Odoo',
            field: 'odoo_data.name',
            render: row => row.odoo_data?.name || 'Pendiente de sincronizar'
        },
        {
            title: 'Stock Odoo',
            field: 'odoo_data.stock',
            render: row => row.odoo_data?.stock ?? 0
        },
        { title: 'Stock ONL', field: 'stock' },
        {
            title: 'Sincronización',
            sorting: false,
            export: false,
            render: stockSyncStatus
        },
        {
            title: 'Sincronizar stock',
            sorting: false,
            export: false,
            render: row => (
                <OdooStockSyncSwitch
                    checked={isOdooStockSyncEnabled(row)}
                    disabled={savingIds.includes(row._id)}
                    label="Permitir"
                    onChange={() => toggleSync(row)}
                    showHelp={false}
                />
            )
        }
    ];
    const unlinkedColumns = [
        { title: 'Producto + variante', field: 'full_name' },
        { title: 'Stock ONL', field: 'stock' },
        {
            title: 'Visibilidad',
            field: 'available',
            render: row => row.available ? 'Visible' : 'Oculta'
        }
    ];
    const catalogColumns = [
        { title: 'ID', field: 'id' },
        { title: 'Nombre', field: 'name' },
        {
            title: 'Precio de venta',
            field: 'list_price',
            render: row => money(row.list_price)
        },
        {
            title: 'Costo',
            field: 'standard_price',
            render: row => money(row.standard_price)
        },
        { title: 'IVA %', field: 'iva' },
        { title: 'Existencia', field: 'stock' }
    ];

    return (
        <Container maxWidth="xl" sx={{ mb: 5, mt: '90px' }}>
            <Alert severity="info">
                <AlertTitle sx={{ fontSize: '24px' }}>
                    Control de inventario Odoo
                </AlertTitle>
                <Typography variant="body1">
                    Las primeras dos tablas muestran las variantes de la tienda.
                    La tercera consulta Odoo en vivo y no guarda una copia local.
                </Typography>
            </Alert>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ my: 3 }}>
                <Alert
                    severity={
                        !syncStatus ?
                            'info'
                        :
                            syncStatus.status === 'error' ? 'warning' : 'success'
                    }
                    sx={{ flex: 1 }}
                >
                    <strong>Última sincronización de inventario:</strong>{' '}
                    {formatDate(syncStatus?.last_success_at)}
                    {syncStatus?.status === 'running' && ' (en ejecución)'}
                    {syncStatus && (
                        <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                            Recibidos: {syncStatus.products_received || 0} ·{' '}
                            Vinculados: {syncStatus.linked_variants || 0} ·{' '}
                            Procesados: {syncStatus.updated_variants || 0}
                        </Typography>
                    )}
                    {syncStatus?.stock_updated_variants !== undefined && (
                        <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                            Stock ONL modificado: {syncStatus.stock_updated_variants || 0} ·{' '}
                            Sin cambios: {syncStatus.unchanged_variants || 0} ·{' '}
                            Desactivadas: {syncStatus.sync_disabled_variants || 0}
                        </Typography>
                    )}
                </Alert>
                <Alert severity={catalogError ? 'error' : 'info'} sx={{ flex: 1 }}>
                    <strong>Catálogo consultado:</strong>{' '}
                    {catalogError ? 'No fue posible consultar Odoo' : formatDate(catalogDate)}
                </Alert>
            </Stack>

            {message && (
                <Alert
                    severity={message.severity}
                    onClose={() => setMessage(null)}
                    sx={{ mb: 3 }}
                >
                    {message.text}
                </Alert>
            )}

            <Box sx={{ mb: 4 }}>
                {!catalogLoading && !catalogError && (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{ mb: 1.5 }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 18, height: 18, backgroundColor: '#E8F5E9' }} />
                            <Typography variant="body2">Encontrada en Odoo</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 18, height: 18, backgroundColor: '#FFEBEE' }} />
                            <Typography variant="body2">No encontrada en Odoo</Typography>
                        </Stack>
                    </Stack>
                )}
                <MaterialTable
                    title={`Variantes vinculadas (${linkedVariants.length})`}
                    data={linkedVariants}
                    columns={linkedColumns}
                    isLoading={localLoading}
                    actions={[{
                        icon: () => <EditIcon />,
                        tooltip: 'Cambiar vinculación',
                        onClick: (event, row) => openLinkDialog(row)
                    }]}
                    options={{
                        ...tableOptions('variantes-vinculadas-odoo.csv'),
                        rowStyle: row => getOdooLinkRowStyle(row, catalogIds, {
                            loading: catalogLoading,
                            error: catalogError
                        })
                    }}
                    localization={tableLocalization}
                />
            </Box>

            <Box sx={{ mb: 4 }}>
                <MaterialTable
                    title={`Variantes sin ID de Odoo (${unlinkedVariants.length})`}
                    data={unlinkedVariants}
                    columns={unlinkedColumns}
                    isLoading={localLoading}
                    actions={[{
                        icon: () => <EditIcon />,
                        tooltip: 'Vincular con Odoo',
                        onClick: (event, row) => openLinkDialog(row)
                    }]}
                    options={tableOptions('variantes-sin-id-odoo.csv')}
                    localization={tableLocalization}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="odoo-stock-filter-label">Existencia</InputLabel>
                        <Select
                            labelId="odoo-stock-filter-label"
                            label="Existencia"
                            value={stockFilter}
                            onChange={({ target }) => setStockFilter(target.value)}
                        >
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="available">Con existencia</MenuItem>
                            <MenuItem value="empty">Sin existencia</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        startIcon={catalogLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                        disabled={catalogLoading}
                        onClick={loadCatalog}
                    >
                        Volver a consultar Odoo
                    </Button>
                </Stack>
                {catalogError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        El catálogo en vivo no está disponible. Las variantes locales
                        siguen accesibles.
                    </Alert>
                )}
                <MaterialTable
                    title={`Catálogo en vivo de Odoo (${visibleCatalog.length})`}
                    data={visibleCatalog}
                    columns={catalogColumns}
                    isLoading={catalogLoading}
                    options={tableOptions('catalogo-odoo-en-vivo.csv')}
                    localization={tableLocalization}
                />
            </Box>

            <Dialog open={Boolean(dialogVariant)} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Vincular variante con Odoo</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {dialogVariant?.full_name}
                    </Typography>
                    <Autocomplete
                        options={catalog}
                        value={selectedProduct}
                        loading={catalogLoading}
                        getOptionLabel={option => `${option.id} · ${option.name}`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(event, value) => {
                            setSelectedProduct(value);
                            setManualId(value ? String(value.id) : '');
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Buscar por ID o nombre"
                                margin="normal"
                            />
                        )}
                    />
                    <TextField
                        fullWidth
                        label="ID de Odoo (captura manual)"
                        margin="normal"
                        value={manualId}
                        onChange={({ target }) => {
                            if (target.value === '' || /^\d+$/.test(target.value)) {
                                setManualId(target.value);
                                setSelectedProduct(null);
                            }
                        }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <OdooStockSyncSwitch
                            checked={dialogSync}
                            disabled={dialogSaving}
                            onChange={({ target }) => setDialogSync(target.checked)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button disabled={dialogSaving} onClick={closeDialog}>
                        Cancelar
                    </Button>
                    <Button
                        disabled={dialogSaving}
                        variant="contained"
                        onClick={saveLink}
                    >
                        {dialogSaving ? 'Guardando…' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default OdooProductList;
